<template>
  <div class="filter-container">
    <div class="filter-title">Ürünleri Filtrele</div>
    <div class="filter-inputs" @keyup.enter="handleApplyFilters">
      <div class="filter-row-top">
        <div class="filter-input-wrapper">
          <Input
            v-model:value="localFilters.product"
            id="product_name"
            label="Ürün adı"
            type="text"
          >
          </Input>
        </div>
        <div class="filter-input-wrapper">
          <Input
            v-model:value="localFilters.stockAmount"
            id="stock_amount"
            label="Stok Adet"
            type="text"
          >
          </Input>
        </div>
        <div class="filter-input-wrapper">
          <Input
            v-model:value="localFilters.stockCode"
            id="stock_code"
            label="Stok Kodu"
            type="text"
          >
          </Input>
        </div>
      </div>
      <div class="filter-row-bottom">
        <div class="filter-input-wrapper">
          <DropdownSelectable
            ref="categoryDropdownRef"
            :dropItems="categories"
            @selectedItems="handleCategoryChange"
            label="Kategori"
            notFoundText="Kategori bulunamadı."
          />
        </div>
        <div class="filter-input-wrapper">
          <DropdownSelectable
            ref="brandDropdownRef"
            :dropItems="brands"
            @selectedItems="handleBrandChange"
            label="Marka"
            notFoundText="Marka bulunamadı."
          />
        </div>
        <div class="filter-buttons">
          <AppButton
            secondary
            md
            title="Temizle"
            @buttonClick="handleClearFilters"
            :disabled="totalSelections < 1"
          />
          <AppButton
            primary
            md
            :title="filterButtonTitle"
            @buttonClick="handleApplyFilters"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Input, DropdownSelectable, AppButton, Icons } from '@n11/so-ui-components';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
    default: () => ({
      product: '',
      stockAmount: '',
      stockCode: '',
      category: [],
      brand: [],
    }),
  },
  categories: {
    type: Array,
    default: () => [],
  },
  brands: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:filters', 'apply-filters', 'clear-filters']);

const localFilters = ref({
  product: props.filters.product || '',
  stockAmount: props.filters.stockAmount || '',
  stockCode: props.filters.stockCode || '',
  category: props.filters.category || [],
  brand: props.filters.brand || [],
});

const categoryDropdownRef = ref(null);
const brandDropdownRef = ref(null);

// Props değiştiğinde localFilters'i güncelle
watch(() => props.filters, (newFilters) => {
  localFilters.value = {
    product: newFilters.product || '',
    stockAmount: newFilters.stockAmount || '',
    stockCode: newFilters.stockCode || '',
    category: newFilters.category || [],
    brand: newFilters.brand || [],
  };
}, { deep: true });

const handleCategoryChange = (selectedItems) => {
  localFilters.value.category = selectedItems;
  emit('update:filters', { ...localFilters.value });
};

const handleBrandChange = (selectedItems) => {
  localFilters.value.brand = selectedItems;
  emit('update:filters', { ...localFilters.value });
};

const totalSelections = computed(() => {
  return (
    (localFilters.value.product ? 1 : 0) +
    (localFilters.value.stockAmount ? 1 : 0) +
    (localFilters.value.stockCode ? 1 : 0) +
    (localFilters.value.category && localFilters.value.category.length > 0 ? 1 : 0) +
    (localFilters.value.brand && localFilters.value.brand.length > 0 ? 1 : 0)
  );
});

const filterButtonTitle = computed(() => {
  return totalSelections.value > 0 ? `Filtrele (${totalSelections.value})` : 'Filtrele';
});

const handleApplyFilters = () => {
  emit('update:filters', { ...localFilters.value });
  emit('apply-filters');
};

const handleClearFilters = () => {
  localFilters.value = {
    product: '',
    stockAmount: '',
    stockCode: '',
    category: [],
    brand: [],
  };
  
  // DropdownSelectable component'lerinin seçimlerini temizle
  if (categoryDropdownRef.value) {
    categoryDropdownRef.value.clearSelection();
  }
  if (brandDropdownRef.value) {
    brandDropdownRef.value.clearSelection();
  }
  
  emit('update:filters', { ...localFilters.value });
  emit('clear-filters');
};

// Expose clear method for parent component
defineExpose({
  clearFilters: handleClearFilters,
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.filter-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 24px;

  .filter-title {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: -0.05px;
    color: $colorN80;
    border: none;
    padding: 0;
  }

  .filter-inputs {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;

    .filter-row-top,
    .filter-row-bottom {
      display: flex;
      gap: 16px;
      width: 100%;
    }

    .filter-input-wrapper {
      position: relative;
      flex: 1;
      width: 100%;
    }

    .filter-buttons {
      display: flex;
      gap: 16px;
      align-items: center;
    }
  }
}
</style>
